/**
 * RequiredAttrsPicker - multi-select of `WorkflowAttributeDefinition`
 * UUIDs, backed by the workflow's attribute catalog (Rewrite §5.5).
 *
 * When the user clicks the action, the engine will capture values for
 * each selected attribute into a `WorkflowInstanceAttributeValue` row.
 * The action's M2M `required_attrs` is the picker's source of truth.
 */

import { useMemo } from 'react'

import { Badge } from '@/shared/components/ui/badge'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { useWorkflowAttributes } from '@/admin/hooks/use-workflow-definitions'

import { readForm, setFormField, type ActionFormContext } from './types'

interface RequiredAttrsPickerProps {
  formCtx: ActionFormContext
  workflowUuid: string
}

export function RequiredAttrsPicker({
  formCtx,
  workflowUuid,
}: RequiredAttrsPickerProps) {
  const form = readForm(formCtx)
  const { data, isLoading } = useWorkflowAttributes(workflowUuid)

  const attributes = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) =>
          a.order - b.order || a.display_name.localeCompare(b.display_name),
      ),
    [data],
  )

  const selected = useMemo(
    () => new Set(form.required_attrs ?? []),
    [form.required_attrs],
  )

  function toggle(uuid: string) {
    const next = new Set(selected)
    if (next.has(uuid)) {
      next.delete(uuid)
    } else {
      next.add(uuid)
    }
    setFormField(formCtx, 'required_attrs', Array.from(next))
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        Required attributes
      </Label>
      <p className="text-[10px] text-[var(--ink-3)]">
        The actor is prompted for these workflow-scoped attributes when they
        run the action. Define new attributes on the workflow's General tab.
      </p>

      {isLoading ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-3 text-center text-[11px] text-[var(--ink-3)]">
          Loading attributes…
        </div>
      ) : attributes.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-3 text-center text-[11px] text-[var(--ink-3)]">
          No attributes defined on this workflow yet.
        </div>
      ) : (
        <div className="flex max-h-[240px] flex-col overflow-y-auto rounded-[6px] border-[0.5px] border-[var(--rule)]">
          {attributes.map((attr) => {
            const checked = selected.has(attr.uuid)
            return (
              <label
                key={attr.uuid}
                className="flex cursor-pointer items-center gap-2 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg)] px-3 py-2 first:border-t-0 hover:bg-[var(--bg-subtle)]"
                htmlFor={`required-attr-${attr.uuid}`}
              >
                <Checkbox
                  id={`required-attr-${attr.uuid}`}
                  checked={checked}
                  onCheckedChange={() => toggle(attr.uuid)}
                />
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="truncate text-[12px] text-[var(--ink)]">
                    {attr.display_name}
                  </span>
                  <Badge variant="outline" className="font-mono text-[10px]">
                    {attr.code_name}
                  </Badge>
                  <Badge
                    variant="secondary"
                    className="text-[10px] uppercase tracking-[0.08em]"
                  >
                    {attr.field_type.toLowerCase().replace(/_/g, ' ')}
                  </Badge>
                </span>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
